import { Avatar, EmptyState, Mono } from './primitives';
import { Sheet } from './Sheet';
import { roleTone } from '../lib/labels';
import { formatRelative } from '../lib/time';
import type { Member, Note } from '../lib/types';

/**
 * The full handoff-note thread, in the shared bottom sheet.
 *
 * The home card shows only the four newest notes (glanceable during a
 * handover); the subscription in db.ts already keeps the latest 30 live, so
 * this sheet is a pure view over data the device already has. Writing stays
 * on the home card — the sheet is for catching up.
 */

/** One note row — shared with the home card so the two can never drift. */
export function NoteRow({
  note,
  author,
  now,
}: {
  note: Note;
  author: Member | undefined;
  now: Date;
}) {
  const tone = roleTone(note.byRole);
  return (
    <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start' }}>
      <Avatar
        initial={author?.initial ?? (note.byRole === 'mom' ? 'マ' : 'パ')}
        color={tone.solid}
        size={26}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          className="cjk"
          style={{ fontSize: 'var(--text-sm)', color: 'var(--text)', overflowWrap: 'anywhere' }}
        >
          {note.text}
        </div>
        <div style={{ fontSize: 10, color: 'var(--text-subtle)', marginTop: 1 }}>
          <Mono>{note.createdAt ? formatRelative(note.createdAt, now) : '送信中…'}</Mono>
        </div>
      </div>
    </div>
  );
}

export function NotesSheet({
  open,
  onClose,
  notes,
  memberByUid,
  now,
}: {
  open: boolean;
  onClose: () => void;
  notes: Note[];
  memberByUid: Map<string, Member>;
  now: Date;
}) {
  return (
    <Sheet open={open} onClose={onClose} eyebrow="Handoff note" title="引き継ぎメモ">
      {notes.length ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '2px 0 16px' }}>
          {notes.map((note) => (
            <NoteRow key={note.id} note={note} author={memberByUid.get(note.byUid)} now={now} />
          ))}
        </div>
      ) : (
        <EmptyState style={{ padding: '6px 0 20px' }}>まだメモはありません。</EmptyState>
      )}
    </Sheet>
  );
}
